import axios from 'axios'
import aqe from '@pushly/aqe'
import type { PageTagScope } from './page-tag-source'
import type { SegmentFieldValueReadRequest, SegmentFieldValueSource } from './segment-field-value-source'

/**
 * Where the keyword field's values are read from.
 *
 * Deliberately the same shape as the subscriber-value source so the two are interchangeable behind one
 * loader: the builder asks "what values can this field hold" and should not have to know that one
 * field's answer lives somewhere else.
 *
 * `key` is accepted and ignored — a keyword is a keyword whichever field asked — rather than dropped
 * from the shape, which would make the two sources non-substitutable for no gain.
 */
function keywordsUrl(scope: PageTagScope): string {
    return scope.level === 'domain'
        ? `${aqe.defaults.publicApiDomain}/domains/${scope.domainId}/keywords`
        : `${aqe.defaults.publicApiDomain}/accounts/${scope.orgId}/keywords`
}

/**
 * Reads a level's keywords, a page at a time.
 *
 * The keyword field is the one catalog entry whose vocabulary is a curated list the domain keeps, not
 * values observed on subscribers, so it is the one field the values table cannot answer for. Its
 * catalog entry carries only the first fifty; without this there is no way to reach the fifty-first.
 *
 * Matching here is substring, not prefix, because that is what this endpoint already does for every
 * other caller. Narrowing it to a prefix would be a change to a shared endpoint made for the benefit
 * of one control.
 */
export function createKeywordValueSource(scope: PageTagScope): SegmentFieldValueSource {
    return {
        async read({ search, offset, limit, signal }: SegmentFieldValueReadRequest): Promise<string[]> {
            // Same signal-to-cancellation bridge the other sources use, for the same reason: the
            // transport predates abort signals.
            const cancellation = axios.CancelToken.source()
            const stop = () => cancellation.cancel()

            if (signal.aborted) stop()
            else signal.addEventListener('abort', stop, { once: true })

            try {
                const { data } = await axios.get(keywordsUrl(scope), {
                    cancelToken: cancellation.token,
                    // This endpoint pages by 1-indexed page number rather than by row, so the row
                    // offset the builder counts in is converted. The builder only ever asks for the
                    // next page after a full one, so the offset is always a whole number of pages.
                    params: {
                        page: Math.floor(offset / limit) + 1,
                        limit,
                        ...(search ? { search } : {}),
                    },
                })

                return Array.isArray(data?.data)
                    ? data.data
                          .map((keyword: { name?: string }) => keyword?.name)
                          .filter((name: unknown): name is string => typeof name === 'string' && name.length > 0)
                    : []
            } finally {
                signal.removeEventListener('abort', stop)
            }
        },
    }
}
